// src/Components/Descargas/PdfInlineRenderer.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Asegúrate de servir el worker en tu dominio (ya lo tienes en IIS)
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs?v=1";
// (si el tuyo es .js => "/pdf.worker.min.js?v=1")

/**
 * props:
 *  - src: URL del PDF (puede llevar ?token=...)
 */
export default function PdfInlineRenderer({ src }) {
  const containerRef = useRef(null);
  const docRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [numPages, setNumPages] = useState(0);

  // ---- ZOOM / AJUSTES ----
  // Arrancamos en modo "fitWidth" para que la página entre completa en pantalla
  // en móvil. Si no, el PDF abre a tamaño nativo (suele ser ~595px de A4) y
  // sale enano + recortado en iPhone hasta que el usuario pulse "Ajustar ancho".
  const [scale, setScale] = useState(1);      // zoom actual
  const [mode, setMode] = useState("fitWidth"); // 'manual' | 'fitWidth'
  const [baseWidth, setBaseWidth] = useState(null); // ancho px de la página a scale=1 (para calcular "Ajustar ancho")

  const deviceRatio = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

  const clearContainer = () => {
    const el = containerRef.current;
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
  };

  const renderAllPages = useCallback(async () => {
    if (!docRef.current || !containerRef.current) return;
    clearContainer();

    const doc = docRef.current;
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale });

      // Lienzo y contenedor de página
      const pageDiv = document.createElement("div");
      pageDiv.className = "pdf-page";
      pageDiv.style.width = `${viewport.width}px`;
      pageDiv.style.height = `${viewport.height}px`;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { alpha: false });

      // Tamaños (CSS px) visibles. NO ponemos maxWidth:100% para que el zoom in
      // pueda crecer por encima del contenedor (con scroll horizontal en .pdf-scroll).
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      // Tamaños reales en píxeles para nitidez en móviles/retina
      canvas.width = Math.floor(viewport.width * deviceRatio);
      canvas.height = Math.floor(viewport.height * deviceRatio);
      context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);

      pageDiv.appendChild(canvas);
      containerRef.current.appendChild(pageDiv);

      await page.render({ canvasContext: context, viewport }).promise;

      // Guarda baseWidth a partir de la primera página
      if (i === 1 && baseWidth == null) {
        const v1 = page.getViewport({ scale: 1 });
        setBaseWidth(v1.width);
      }
    }
  }, [scale, deviceRatio, baseWidth]);

  // Cargar documento
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const task = pdfjsLib.getDocument({ url: src });
        const doc = await task.promise;
        if (cancelled) return;

        docRef.current = doc;
        setNumPages(doc.numPages);

        // Calcular ancho base con la primera página para arrancar en fit-width
        // ANTES del primer render, así no parpadea de "tamaño nativo" a "ajustado".
        try {
          const firstPage = await doc.getPage(1);
          const v1 = firstPage.getViewport({ scale: 1 });
          if (!cancelled) setBaseWidth(v1.width);

          const cw = containerRef.current?.clientWidth || 0;
          if (!cancelled && mode === "fitWidth" && cw > 0 && v1.width > 0) {
            setScale(cw / v1.width);
            // El useEffect de [scale] disparará el render. Salimos.
            return;
          }
        } catch (_) { /* si falla, caemos al render normal */ }

        await renderAllPages();
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError("No se pudo abrir el PDF.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try { docRef.current?.destroy(); } catch {}
      docRef.current = null;
      clearContainer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Re-render al cambiar scale
  useEffect(() => {
    if (!docRef.current) return;
    (async () => { 
      setLoading(true);
      await renderAllPages();
      setLoading(false);
    })();
  }, [scale, renderAllPages]);

  // Ajuste a ancho al redimensionar si está activo "fitWidth"
  useEffect(() => {
    const onResize = () => {
      if (mode !== "fitWidth" || baseWidth == null || !containerRef.current) return;
      const cw = containerRef.current.clientWidth;
      if (cw > 0) setScale(cw / baseWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mode, baseWidth]);

  // ---- Acciones de la barra ----
  const MIN = 0.5, MAX = 3, STEP = 0.1;

  const zoomOut = () => {
    setMode("manual");
    setScale(s => Math.max(MIN, +(s - STEP).toFixed(2)));
  };
  const zoomIn = () => {
    setMode("manual");
    setScale(s => Math.min(MAX, +(s + STEP).toFixed(2)));
  };
  const resetZoom = () => {
    setMode("manual");
    setScale(1);
  };
  const fitWidth = () => {
    if (!containerRef.current || baseWidth == null) return;
    const cw = containerRef.current.clientWidth;
    if (cw > 0) {
      setMode("fitWidth");
      setScale(cw / baseWidth);
    }
  };

  return (
    <div className="pdf-shell">
      <div className="pdf-toolbar">
        <div className="pdf-toolbar-left">
          <button onClick={zoomOut} aria-label="Disminuir zoom">−</button>
          <span className="pdf-zoom">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} aria-label="Aumentar zoom">+</button>
          <button onClick={resetZoom} title="100%">100%</button>
          <button onClick={fitWidth} title="Ajustar ancho">⤢</button>
        </div>
        <div className="pdf-toolbar-right">
          <span>{numPages ? `Páginas: ${numPages}` : ""}</span>
        </div>
      </div>

      <div className="pdf-scroll">
        {loading && <div className="wv-msg">Cargando documento…</div>}
        {error && <div className="wv-msg">{error}</div>}
        <div ref={containerRef} />
      </div>
    </div>
  );
}
